import { AddCircleOutline, Edit, People } from "@mui/icons-material";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField } from "@mui/material";
import { GridActionsCellItem, GridColDef, GridRowParams } from "@mui/x-data-grid";
import Container from "components/Container";
import Loading from "components/Loading";
import Table from "components/Table";
import Main from "layouts/Main";
import React from "react";
import { useTranslation } from "react-i18next";
import { createOrganization, getOrganizationsByAdminId, modifyOrganizationName } from "services/api";

const OrganizationList = () => {
	const { t } = useTranslation(["common"]);

	const [open, setOpen] = React.useState(false);
	const [organizationName, setOrganizationName] = React.useState("");
	const [selectedOrganizationId, setSelectedOrganizationId] = React.useState(null);
	const [rows, setRows] = React.useState([]);

	const [isLoading, setIsLoading] = React.useState(false);
	const columns: GridColDef[] = [
		{
			field: "organizationId",
			headerName: t("organizationId"),
			flex: 1,
		},
		{
			field: "organizationName",
			headerName: t("organizationName"),
			flex: 1,
		},
		{
			field: "createTime",
			headerName: t("createTime"),
			flex: 1,
			valueFormatter: (value?: string) => {
				return new Date(value).toLocaleString();
			},
		},
		{
			field: "actions",
			type: "actions",
			getActions: (params: GridRowParams) => [
				<GridActionsCellItem
					icon={<People />}
					onClick={() => {
						window.location.href = `/app/organization/userList/${params.row.organizationId}`;
					}}
					label={t("userList")}
					showInMenu
				/>,
				<GridActionsCellItem
					icon={<Edit />}
					onClick={() => {
						setOrganizationName(params.row.organizationName);
						setSelectedOrganizationId(params.row.organizationId);
						setOpen(true);
					}}
					label={t("edit")}
					showInMenu
				/>,
			],
		},
	];

	const handleChange = (rows: any[]) => {
		setRows(rows);
	};

	const handleClose = () => {
		setOpen(false);
		setSelectedOrganizationId(null);
		setOrganizationName("");
	};

	const handleUpdate = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const formData = new FormData(event.currentTarget);
		const formJson = Object.fromEntries((formData as any).entries());

		const organizationName: string = formJson.organizationName;

		if (selectedOrganizationId) {
			modifyOrganizationName(selectedOrganizationId, organizationName).then((resp) => {
				if (resp.info) {
					updateOrganizationList().finally(() => handleClose());
				}
			});
		} else {
			createOrganization(organizationName).then((resp) => {
				if (resp.info) {
					updateOrganizationList().finally(() => handleClose());
				}
			});
		}
	};
	const updateOrganizationList = () => {
		return getOrganizationsByAdminId().then((resp) => {
			if (!resp || resp?.error) return;
			setRows(resp);
		});
	};

	React.useEffect(() => {
		updateOrganizationList().finally(() => setIsLoading(false));
	}, []);

	return (
		<Main title={t("allOrganization")}>
			{isLoading ? (
				<Loading />
			) : (
				<Container
					maxWidth={1080}
					px={3}>
					<Box>
						<Box
							width={1}
							display="flex"
							justifyContent={"flex-end"}>
							<IconButton
								size="small"
								onClick={() => setOpen(true)}>
								<AddCircleOutline />
							</IconButton>
						</Box>
					</Box>
					<Table
						loading={isLoading}
						columns={columns}
						rows={rows}
						handleChange={handleChange}
						getRowId={(row) => {
							return row.organizationId;
						}}
					/>

					<Dialog
						open={open}
						onClose={handleClose}
						fullWidth
						maxWidth={"sm"}
						disableEscapeKeyDown
						slotProps={{
							paper: {
								component: "form",
								onSubmit: handleUpdate,
							},
						}}>
						<DialogTitle>{t(selectedOrganizationId ? "editOrganization" : "createOrganization")}</DialogTitle>
						<DialogContent>
							{/* <DialogContentText>
								To subscribe to this website, please enter your email address here. We will send updates occasionally.
							</DialogContentText> */}
							<TextField
								autoFocus
								required
								margin="dense"
								id="organizationName"
								name="organizationName"
								label={t("organizationName")}
								value={organizationName}
								onChange={(e) => {
									setOrganizationName(e.target.value);
								}}
								fullWidth
								variant="outlined"
							/>
						</DialogContent>
						<DialogActions>
							<Button
								onClick={handleClose}
								color="inherit">
								{t("cancel")}
							</Button>
							<Button
								type="submit"
								variant="contained">
								{t("submit")}
							</Button>
						</DialogActions>
					</Dialog>
				</Container>
			)}
		</Main>
	);
};

export default OrganizationList;
